<template>
  <div v-if="isLoad" :class="['certificates-page', { 'certificates-page--iframe': isIframe }]">
    <div v-if="isLoading" class="certificates-page__loader">
      <div class="certificates-page__spinner"/>
    </div>

    <section :class="{ 'container-disabled': isLoading }">
      <h1 v-if="pageData.title" class="certificates-page__title">
        {{ pageData.title }}
      </h1>

      <div :class="['certificates-page__search', { 'certificates-page__search--no-title': !pageData.title }]">
        <form class="certificates-page__search-form" @submit.prevent="submitSearch">
          <div class="certificates-page__search-input-wrap">
            <BaseInput
              ref="vinInput"
              :value="form.vin"
              :label="pageData.form && pageData.form.vin.label"
              :required="pageData.form && pageData.form.vin.required"
              :errorMsg="errorMsgs.vin"
              class="certificates-page__search-input"
              max="17"
              validatePatternVin
              @input="onVinInput"
            />
            <div>
              <a
                v-if="pageData.vinHintText && pageData.vinHintUrl"
                class="certificates-page__search-hint certificates-page__search-hint--link"
                :href="pageData.vinHintUrl"
                target="_blank"
              >
                {{ pageData.vinHintText }}
              </a>
              <span
                v-else-if="pageData.vinHintText"
                class="certificates-page__search-hint"
              >
                {{ pageData.vinHintText }}
              </span>
            </div>
          </div>
          <BaseButton
            :disabled="!form.vin"
            class="certificates-page__search-button"
            buttonNativeType="submit"
          >
            {{ pageData.submitButtonText }}
          </BaseButton>
        </form>
      </div>

      <div v-if="files.length" class="certificates-page__results">
        <div v-if="pageData.resultsTitle" class="certificates-page__results-title">
          {{ pageData.resultsTitle }}
          <span v-if="pageData.resultsSubTitle">{{ pageData.resultsSubTitle }}</span>
        </div>
        <div class="certificates-page__results-list">
          <a
            v-for="(file, index) in files"
            :key="index"
            :href="file.url"
            class="certificates-page__card"
            target="_blank"
            download
          >
            <div class="certificates-page__card-info">
              <div class="certificates-page__card-name-row">
                <span class="certificates-page__card-name">{{ file.label }}</span>
                <span v-if="file.ext" class="certificates-page__card-ext">.{{ file.ext }}</span>
              </div>
              <span class="certificates-page__card-size">{{ formatSize(file.size) }}</span>
            </div>
            <div class="certificates-page__card-download">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.3496 15.7002C14.5705 15.7002 14.75 15.8797 14.75 16.1006C14.7498 16.3213 14.5704 16.5 14.3496 16.5H5.65039C5.42961 16.5 5.25021 16.3213 5.25 16.1006C5.25 15.8797 5.42948 15.7002 5.65039 15.7002H14.3496ZM10.002 3.5C10.2227 3.50021 10.4014 3.67961 10.4014 3.90039V13.1055L13.5029 10.0039C13.659 9.84794 13.9122 9.8481 14.0684 10.0039C14.2246 10.1601 14.2246 10.4141 14.0684 10.5703L10.8652 13.7734C10.3966 14.242 9.63657 14.242 9.16797 13.7734L5.96484 10.5703C5.80863 10.4141 5.80863 10.1601 5.96484 10.0039C6.12096 9.84797 6.37409 9.84807 6.53027 10.0039L9.60156 13.0752V3.90039C9.60156 3.67948 9.78104 3.5 10.002 3.5Z" fill="#282830"/>
              </svg>
            </div>
          </a>
        </div>
      </div>

      <div v-if="errorText" class="certificates-page__error">
        <div class="certificates-page__error-text">{{ errorText }}</div>
      </div>

      <div v-if="noResults" class="certificates-page__no-results">
        <div class="certificates-page__no-results-title">
          {{ noResults.title }}
        </div>
        <div v-if="noResults.text" class="certificates-page__no-results-text">
          {{ noResults.text }}
        </div>
      </div>

      <div v-if="hasFinalBlock" class="certificates-page__final">
        <div v-if="finalBlock.text && files.length" class="certificates-page__final-text" v-html="finalBlock.text" />
        <a
          v-if="finalBlock.button && finalBlock.button.url && finalBlock.button.label"
          :href="finalBlock.button.url"
          class="button button_main certificates-page__final-button"
          target="_blank"
        >
          {{ finalBlock.button.label }}
        </a>
      </div>

      <div
        v-if="pageData.description"
        class="certificates-page__description"
        v-html="pageData.description"
      />
    </section>
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'

  export default {
    name: 'Certificate',

    data () {
      return {
        pageData: {},
        form: {
          vin: ''
        },
        files: [],
        noResults: null,
        errorText: null,
        finalBlock: null,
        errorMsgs: {},
        isLoading: false,
        recaptchaReady: null
      }
    },

    computed: {
      ...mapGetters(['isLoad']),
      ...mapState(['currentLang', 'currentCountry']),
      // Detect if the page is rendered inside an iframe to remove top padding
      isIframe () {
        try {
          return window.self !== window.top
        } catch (e) {
          return true
        }
      },
      hasFinalBlock () {
        if (!this.finalBlock) return false
        const f = this.finalBlock
        return f.text || (f.button && f.button.url && f.button.label)
      }
    },

    watch: {
      isLoad (val) {
        if (val) {
          this.$nextTick(() => {
            const inputEl = this.$refs.vinInput && this.$refs.vinInput.$refs.input
            if (inputEl) {
              inputEl.addEventListener('paste', this.onVinPaste)
            }
          })
        }
      }
    },

    beforeDestroy () {
      const inputEl = this.$refs.vinInput && this.$refs.vinInput.$refs.input
      if (inputEl) {
        inputEl.removeEventListener('paste', this.onVinPaste)
      }
    },

    created () {
      if (this._.get(this.$route, 'query.country')) {
        this.$store.commit('setCurrentCountry', this.removeQueryString(this.$route.query.country))
      }

      if (this._.get(this.$route, 'query.lang')) {
        this.$store.commit('setCurrentLang', this.removeQueryString(this.$route.query.lang))
      }

      this.fetchPageData()
    },

    methods: {
      onVinInput (value) {
        this.form.vin = value.trim().toUpperCase().replace(/[^A-Z0-9]/gi, '').slice(0, 17)
        this.errorMsgs = {}
      },

      onVinPaste (e) {
        e.preventDefault()
        const paste = (e.clipboardData || window.clipboardData).getData('text') || ''
        const filtered = paste.trim().toUpperCase().replace(/[^A-Z0-9]/gi, '').slice(0, 17)
        this.form.vin = filtered
        const inputComp = this.$refs.vinInput
        if (inputComp) {
          inputComp.currentValue = filtered
        }
        e.target.value = filtered
        this.errorMsgs = {}
      },

      isValidVin (vin) {
        return /^([0-9A-HJ-NPR-Z]{9})([A-HJ-NPR-TV-Y0-9])([0-9A-HJ-NPR-Z])([0-9A-HJ-NPR-Z]{2})(\d{4})$/i.test(vin)
      },

      formatSize (bytes) {
        if (!bytes && bytes !== 0) return ''
        if (bytes < 1024) return bytes + ' B'
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
      },

      removeQueryString (url) {
        if (url) {
          return url.split('?')[0]
        }
        return url
      },

      async fetchPageData () {
        try {
          const { data } = await window.axios.get(window.location.pathname)
          this.pageData = data
          this.$store.commit('setGeneralData', data)

          if (data.recaptchaSiteKey) {
            this.loadRecaptcha(data.recaptchaSiteKey)
          }

          if (data.scripts) {
            this.execScripts(data.scripts)
          }
        } catch (e) {
          console.error('Failed to fetch page data', e)
          this.$router.push({ name: 'not-found' })
        } finally {
          this.$store.commit('setIsLoad')
        }
      },

      execScripts (scripts) {
        if (scripts.head && scripts.head.end) {
          this.insertScriptBlock(scripts.head.end, document.head)
        }
        if (scripts.body && scripts.body.begin) {
          this.insertScriptBlock(scripts.body.begin, document.body, true)
        }
        if (scripts.body && scripts.body.end) {
          this.insertScriptBlock(scripts.body.end, document.body)
        }
      },

      insertScriptBlock (html, parent, prepend) {
        const container = document.createElement('div')
        container.innerHTML = html
        const scripts = container.querySelectorAll('script')
        scripts.forEach((s) => {
          const script = document.createElement('script')
          if (s.src) {
            script.src = s.src
            script.async = true
          } else {
            script.textContent = s.textContent
          }
          if (prepend && parent.firstChild) {
            parent.insertBefore(script, parent.firstChild)
          } else {
            parent.appendChild(script)
          }
        })
      },

      loadRecaptcha (siteKey) {
        if (this.recaptchaReady) return

        this.recaptchaReady = new Promise((resolve) => {
          const script = document.createElement('script')
          script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`
          script.async = true
          script.onload = () => {
            window.grecaptcha.ready(() => resolve(true))
          }
          script.onerror = () => resolve(false)
          document.head.appendChild(script)
        })
      },

      async getRecaptchaToken () {
        const siteKey = this.pageData.recaptchaSiteKey
        if (!siteKey || !this.recaptchaReady) return null

        try {
          const loaded = await this.recaptchaReady
          if (!loaded) return null

          const action = this.pageData.recaptchaAction || 'certificate_check'
          return await window.grecaptcha.execute(siteKey, { action })
        } catch (e) {
          console.error('reCAPTCHA error', e)
          return null
        }
      },

      handleResponse (data) {
        if (data.final) {
          this.finalBlock = data.final
        }

        if (data.status === 'found' && data.files && data.files.length) {
          this.files = data.files
        } else if (data.status === 'not_found') {
          this.noResults = {
            title: this.pageData.noResultsTitle,
            text: this.pageData.noResultsSubTitle
          }
        } else if (data.status === 'error' || data.status === 'limit') {
          if (data.error && data.error.text) {
            this.errorText = data.error.text
          }
        }
      },

      resetResults () {
        this.files = []
        this.noResults = null
        this.errorText = null
        this.finalBlock = null
      },

      async submitSearch () {
        this.errorMsgs = {}

        if (this.form.vin.length !== 17) {
          const msg = (this.pageData.form && this.pageData.form.vin && this.pageData.form.vin.lengthError) || 'VIN-код повинен містити 17 символів'
          this.$set(this.errorMsgs, 'vin', msg)
          if (this.$refs.vinInput) {
            this.$refs.vinInput.error = true
          }
          return
        }

        if (!this.isValidVin(this.form.vin)) {
          const msg = (this.pageData.form && this.pageData.form.vin && this.pageData.form.vin.validationError) || 'Невірний формат VIN-коду'
          this.$set(this.errorMsgs, 'vin', msg)
          if (this.$refs.vinInput) {
            this.$refs.vinInput.error = true
          }
          return
        }

        this.resetResults()
        this.isLoading = true

        try {
           const recaptchaToken = await this.getRecaptchaToken()
          // const recaptchaToken = 'ggggggg'

          const { data } = await window.axios.post('/certificate-check', {
            vin: this.form.vin,
            ...(recaptchaToken && { recaptchaToken })
          })

          this.handleResponse(data)
        } catch (e) {
          try {
            const data = e.response.data
            console.log('[Certificate] catch data:', JSON.stringify(data))

            if (data.vin) {
              const msg = Array.isArray(data.vin) ? data.vin[0] : data.vin
              this.$set(this.errorMsgs, 'vin', msg)
            }

            this.handleResponse(data)
          } catch (inner) {
            console.error('[Certificate] failed to parse error response', e, inner)
          }
        } finally {
          this.isLoading = false
        }
      }
    }
  }
</script>

<style lang="scss">
.certificates-page {
  position: relative;
  max-width: 1440px;
  margin: 0 auto;
  padding: 120px 150px;

  @include tablet {
    padding: 64px 24px;
  }

  @include mobile {
    padding: 48px 16px;
  }

  &--iframe {
    padding-top: 0;

    @include tablet {
      padding-top: 0;
    }

    @include mobile {
      padding-top: 0;
    }
  }

  &__title {
    font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
    font-size: 40px;
    font-weight: 400;
    line-height: 48px;
    color: $c-gray-dark;
    text-align: center;
    text-transform: uppercase;
    margin-bottom: 40px;

    @include tablet {
      font-size: 34px;
      line-height: 40px;
      text-transform: none;
    }

    @include mobile {
      font-size: 34px;
      line-height: 40px;
      text-align: left;
    }
  }

  &__search {
    padding-top: 32px;
    padding-bottom: 26px;
    border-top: 1px solid $c-gray-6;
    border-bottom: 1px solid $c-gray-6;
    display: flex;
    justify-content: center;

    &--no-title {
      border-top: none;
    }

    @include tablet {
      padding-top: 24px;
      padding-bottom: 20px;
    }

    @include mobile {
      padding-top: 24px;
      padding-bottom: 22px;
    }

    &-form {
      display: flex;
      align-items: flex-start;
      gap: 16px;
      width: 558px;

      @include mobile {
        flex-direction: column;
        width: 100%;
        gap: 20px;
      }
    }

    &-input-wrap {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;

      @include mobile {
        width: 100%;
      }

      .input__field {
        padding: 25px 16px 5px;
        text-transform: uppercase;
      }
    }

    &-input {
      width: 100%;

      .input__error-msg {
        width: 100%;
      }

      &.input-wrapper {
        padding-bottom: 0;
        min-width: 0;

        .input__error-msg {
          position: unset;
          display: inline-flex;
          gap: 30px;
        }
      }
    }

    &-hint {
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      font-size: 13px;
      line-height: 20px;
      color: $c-gray-dark;
      display: inline-flex;

      &--link {
        text-decoration: underline;
        text-underline-offset: 2px;

        @include hover {
          color: $c-gray-dark;
          text-decoration: none;
        }

        &:focus {
          color: $c-gray-dark;
        }
      }
    }

    &-button {
      flex-shrink: 0;
      height: 56px;

      @include mobile {
        width: 100%;
        height: 48px;
      }
    }
  }

  &__results {
    margin: 40px 0 0;
    display: flex;
    flex-direction: column;
    align-items: center;

    @include tablet {
      margin-top: 30px;
    }

    @include mobile {
      margin-top: 34px;
      gap: 12px;
    }

    &-title {
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      font-size: 22px;
      font-weight: 400;
      line-height: 30px;
      color: $c-gray-dark;
      margin-bottom: 32px;
      text-align: center;

      @include mobile {
        font-size: 22px;
        line-height: 28px;
        margin-bottom: 20px;
      }

      span {
        display: block;
        font-size: 16px;
        line-height: 24px;
        color: $c-gray-7;

        @include mobile {
          line-height: 26px;
        }
      }
    }

    &-list {
      max-width: 558px;
      display: flex;
      flex-direction: column;
      align-self: center;
      width: 100%;

      @include mobile {
        max-width: 100%;
        align-self: stretch;
      }
    }
  }

  &__card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    border-top: 1px solid $c-gray-4;
    width: 100%;
    text-decoration: none;
    transition: border-color 0.3s;
    cursor: pointer;

    &:last-child {
      border-bottom: 1px solid $c-gray-4;
    }

    @include hover {
      .certificates-page__card-info,
      .certificates-page__card-download {
        opacity: 0.7;
      }
    }

    &-info {
      display: flex;
      flex-direction: column;
      min-width: 0;
      flex: 1;
      gap: 0;
    }

    &-name-row {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    &-name {
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      font-size: 16px;
      line-height: 18.5px;
      color: $c-gray-dark;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &-ext {
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      font-size: 13px;
      line-height: 13px;
      color: $c-gray-6;
      flex-shrink: 0;
    }

    &-size {
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      font-size: 13px;
      line-height: 13px;
      color: $c-gray-6;
    }

    &-download {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 16px;
    }
  }

  &__final {
    padding-top: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 25px;
    padding-bottom: 32px;
    border-bottom: 1px solid $c-gray-6;

    @include tablet {
      padding-top: 32px;
    }

    @include mobile {
      gap: 22px;
      padding-bottom: 24px;
    }

    &-text {
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      font-size: 16px;
      line-height: 23px;
      color: $c-gray-dark;

      p {
        line-height: 24px;
      }

      strong,
      b {
        font-family: ToyotaType-Semibold, Arial, Helvetica, sans-serif;
        font-weight: 700;
      }

      ul,
      ol {
        padding-left: 40px;

        @include mobile {
          padding-left: 30px;
        }
      }

      a {
        font-size: 16px;

        @include hover {
          color: $c-gray-dark;
          text-decoration: none;
        }

        &:focus {
          color: $c-gray-dark;
        }
      }

      blockquote {
        margin: 16px 0;
        padding-left: 20px;
        border-left: 3px solid $c-gray-6;
        color: $c-gray-7;
        font-style: italic;
      }
    }

    &-button.button {
      text-decoration: none;
      height: 48px;
      line-height: 13px;

      @include hover {
        color: $c-white;
        background-color: $c-gray-7;
      }

      &:focus {
        color: $c-white;
      }

      @include mobile {
        width: 100%;
      }
    }
  }

  &__error {
    text-align: center;
    padding: 40px 0 0;

    &-text {
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      font-size: 22px;
      font-weight: 400;
      line-height: 30px;
      color: $c-errorDark;
    }
  }

  &__no-results {
    text-align: center;
    padding: 40px 0 0;

    &-title {
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      font-size: 22px;
      font-weight: 400;
      line-height: 30px;
      color: $c-errorDark;
      margin-bottom: 0;
    }

    &-text {
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      font-size: 16px;
      line-height: 24px;
      color: $c-errorDark;
    }
  }

  &__description {
    margin-top: 40px;
    font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
    font-size: 16px;
    line-height: 24px;
    color: $c-gray-dark;

    p {
      line-height: 24px;
    }

    strong,
    b {
      font-family: ToyotaType-Semibold, Arial, Helvetica, sans-serif;
      font-weight: 700;
    }

    ul,
    ol {
      padding-left: 40px;

      @include mobile {
        padding-left: 30px;
      }
    }

    a {
      font-size: 16px;

      @include hover {
        color: $c-gray-dark;
        text-decoration: none;
      }

      &:focus {
        color: $c-gray-dark;
      }
    }

    blockquote {
      margin: 16px 0;
      padding-left: 20px;
      border-left: 3px solid $c-gray-6;
      color: $c-gray-7;
      font-style: italic;
    }

    @include tablet {
      margin-top: 32px;
    }

    p + p {
      margin-top: 24px;

      @include tablet {
        margin-top: 16px;
      }
    }
  }

  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba($c-white, 0.85);
    z-index: 10;
  }

  &__spinner {
    width: 50px;
    height: 50px;
    border: 2px solid transparent;
    border-top-color: $c-gray-dark;
    border-radius: 50%;
    animation: certificates-spin 1s linear infinite;
  }
}

@keyframes certificates-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
